/**
 * Cron / Billing tab — trigger the recurring-billing engine on demand and
 * render the resulting counters + event log. Also surfaces the cron
 * dead-man's-switch heartbeat status for every long-lived background sweep.
 */
import { useCallback, useEffect, useState } from "react";
import { Play, RefreshCw, HeartPulse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function CronBillingTab({ busy, cronStats, onRun }) {
    return (
        <div className="space-y-4">
            <div className="border border-border bg-card rounded p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Play className="w-4 h-4 text-primary" />
                    <div className="overline">Recurring billing engine</div>
                </div>
                <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
                    Runs automatically every 24 hours in a background asyncio task. Scans institutional
                    agencies whose 30-day monthly billing anniversary is due and marks them for admin
                    bank-transfer verification. Individual accounts are free forever and are never touched
                    by this cron. Institutional agencies with unpaid renewals past their grace window
                    are moved to <span className="mono">plan_type=expired</span> and blocked from new-seat onboarding.
                </p>
                <Button onClick={onRun} disabled={busy} data-testid="run-cron-btn">
                    {busy ? "Running…" : "Run daily billing now"}
                </Button>
                {cronStats && (
                    <div className="mt-4 grid md:grid-cols-3 gap-3 text-sm">
                        <StatBlock label="Institutions scanned" value={cronStats.processed} />
                        <StatBlock label="Renewals due" value={cronStats.credits_consumed} accent="text-accent" />
                        <StatBlock
                            label="Marked expired"
                            value={cronStats.stk_failed + cronStats.trial_downgraded}
                            accent="text-danger"
                        />
                    </div>
                )}
                {cronStats?.events?.length > 0 && (
                    <div className="mt-4">
                        <div className="overline mb-2">Event log (this run)</div>
                        <div className="text-xs mono space-y-1 max-h-[300px] overflow-y-auto">
                            {cronStats.events.map((e, i) => (
                                <div key={`${e.ts || e.action}-${i}`} className="border-b border-border/50 pb-1">
                                    {e.action} · user={e.user?.slice(-6)}
                                    {e.amount ? ` · KES ${e.amount}` : ""}
                                    {e.remaining !== undefined ? ` · ${e.remaining} credits left` : ""}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <CronHeartbeatsPanel />
        </div>
    );
}

/* ─── Cron heartbeats (dead-man's-switch) ───────────────────────────────── */
const _STATUS_TONE = {
    ok:             "text-primary",
    stale:          "text-danger",
    never_reported: "text-muted-foreground",
};

function CronHeartbeatsPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get("/admin/ops/cron-heartbeats");
            setData(data);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            toast.error("Failed to load cron heartbeats.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="border border-border bg-card rounded p-4" data-testid="cron-heartbeats-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <HeartPulse className="w-4 h-4 text-primary" />
                    <div className="overline">Cron heartbeats</div>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded border border-border hover:bg-muted/50 disabled:opacity-50"
                    data-testid="cron-heartbeats-refresh"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>
            <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
                Every long-lived background sweep reports here once per tick. A job flagged
                <span className="text-danger font-semibold"> STALE</span> has stopped ticking — investigate before its
                downstream effect (e.g. a stop-loss that never fires) surfaces as a user complaint.
            </p>
            {error && (
                <div className="p-3 rounded border border-destructive/40 bg-destructive/5 text-xs text-destructive" data-testid="cron-heartbeats-error">
                    {error}
                </div>
            )}
            {loading || !data ? (
                <div className="text-xs text-muted-foreground">Loading…</div>
            ) : (
                <>
                    {data.stale_count > 0 && (
                        <div className="mb-3 p-2 rounded border border-destructive/40 bg-destructive/5 text-xs text-destructive font-semibold" data-testid="cron-heartbeats-stale-banner">
                            {data.stale_count} job(s) stale — not reporting on schedule.
                        </div>
                    )}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {(data.jobs || []).map((j) => (
                            <div key={j.job} className="border border-border/60 rounded p-2.5 text-xs" data-testid={`cron-job-${j.job}`}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-mono font-semibold truncate">{j.job}</span>
                                    <span className={`font-bold uppercase text-[10px] ${_STATUS_TONE[j.status] || ""}`} data-testid={`cron-job-${j.job}-status`}>
                                        {j.status.replace("_", " ")}
                                    </span>
                                </div>
                                <div className="text-muted-foreground">
                                    {j.last_beat_at ? `${j.seconds_since}s ago` : "never reported"} · expects every {j.expected_interval_sec}s
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function StatBlock({ label, value, accent = "" }) {
    return (
        <div className="border border-border rounded p-3">
            <div className={`overline ${accent}`}>{label}</div>
            <div className={`mono text-2xl font-bold ${accent}`}>{value}</div>
        </div>
    );
}
